import { Page, SignatureRunLog } from '../../../../types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getSignatureLogs, UserKeys } from '../../../api/users-api';
import { useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import AvatarChip from '../../../shared/avatar-chip';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import History from '@mui/icons-material/History';
interface SharedSignatureTemplateGridLogsProps {
  signatureId: number;
  wrapperSx?: Record<string, unknown>;
}

const DetailPanel = ({ row }: { row: SignatureRunLog }) => {
  const [filterInput, setFilterInput] = useState<string>('');
  const [viewType, setViewType] = useState<'updated' | 'errored'>('updated');

  const targetEmails = row.targetEmails || [];
  const pushedToEmails = row.pushedToEmails || [];

  const pushedSet = useMemo(() => new Set(pushedToEmails), [pushedToEmails]);
  const erroredCount = targetEmails.length - pushedToEmails.length;

  const filteredEmails = useMemo(() => {
    const searchLower = filterInput.toLowerCase();
    const sourceArray =
      viewType === 'updated'
        ? pushedToEmails
        : targetEmails.filter((email) => !pushedSet.has(email));

    if (!searchLower) return sourceArray;

    return sourceArray.filter((email) => email.includes(searchLower));
  }, [viewType, filterInput, pushedToEmails, targetEmails, pushedSet]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <RadioGroup
          row
          value={viewType}
          onChange={(e) => setViewType(e.target.value as 'updated' | 'errored')}
        >
          <FormControlLabel
            value="updated"
            control={<Radio />}
            label={`Updated Users (${pushedToEmails.length})`}
          />
          <FormControlLabel
            value="errored"
            control={<Radio />}
            label={`Errored Users (${erroredCount})`}
          />
        </RadioGroup>

        <Stack flexDirection="row" gap={1} alignItems="center">
          <Typography>
            {viewType === 'updated' ? 'Users Updated' : 'Users Errored'} (
            {viewType === 'updated' ? pushedToEmails.length : erroredCount}):
          </Typography>
          <TextField
            label="Search"
            sx={{ width: 300 }}
            size="small"
            value={filterInput}
            onChange={(e) => setFilterInput(e.target.value)}
            variant="outlined"
          />
        </Stack>

        <Stack
          flexDirection="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ mt: 1, maxHeight: 320, overflowY: 'auto', p: 1 }}
        >
          {filteredEmails.map((email) => (
            <AvatarChip key={email} email={email} type={'User'} />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default function SharedSignatureTemplateGridLogs({
  signatureId,
  wrapperSx,
}: SharedSignatureTemplateGridLogsProps) {
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
  }>({
    pageSize: 25,
    page: 0,
    cursor: null,
  });

  const {
    data: signatureTemplateLogs,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      UserKeys.SignatureRunLogs,
      signatureId,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.cursor,
    ],
    queryFn: () => {
      const object: Page<SignatureRunLog[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      return getSignatureLogs(object, signatureId);
    },
    placeholderData: keepPreviousData,
  });

  const columns: GridColDef<SignatureRunLog>[] = useMemo(
    () => [
      {
        field: 'startTime',
        headerName: 'Start Time',
        flex: 2,
        type: 'dateTime',
        valueGetter: (value) => (value ? new Date(value) : null),
        valueFormatter: (params: Date) =>
          params ? dayjs(params).format('MMM D, YYYY, h:mm A') : '',
      },
      {
        field: 'endTime',
        headerName: 'End Time',
        flex: 2,
        type: 'dateTime',
        valueGetter: (value: number | null) =>
          value === null || value === 0 ? null : new Date(value),
        valueFormatter: (params: Date | null) =>
          params
            ? dayjs(params).format('MMM D, YYYY, h:mm A')
            : 'still running',
      },
      {
        field: 'isManual',
        headerName: 'Manual',
        flex: 2,
        type: 'boolean',
      },
      {
        field: 'pushedToEmails',
        headerName: 'Users Updated',
        flex: 2,
        valueGetter: (value: string[] | null) => value?.length || 0,
      },
      {
        field: 'targetEmails',
        headerName: 'Users Errored',
        flex: 2,
        valueGetter: (_value, row) =>
          (row.targetEmails?.length || 0) - (row.pushedToEmails?.length || 0),
      },
      {
        field: 'totalEmails',
        headerName: 'Total Users Targeted',
        flex: 2,
        valueGetter: (_value, row) => row.targetEmails?.length || 0,
      },
    ],
    []
  );

  return (
    <Box sx={{ height: 500, ...wrapperSx }}>
      <SharedDataGridServer
        showToolbar={false}
        slots={{
          ...createGridOverlays('Logs', <History />),
        }}
        data={signatureTemplateLogs}
        getDetailPanelContent={({ row }) => <DetailPanel row={row} />}
        getDetailPanelHeight={() => 400}
        columns={columns}
        sx={{
          mt: 1,
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={{
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
        }}
        onPaginationModelChange={(newModel) => {
          if (isLoading || isFetching) return;
          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
            });
          } else {
            setPaginationModel({
              page: newModel.page,
              pageSize: newModel.pageSize,
              cursor: signatureTemplateLogs?.cursor || '',
            });
          }
        }}
        loading={isLoading || isFetching}
        disableColumnSelector
        disableRowSelectionOnClick
      />
    </Box>
  );
}
